import { useEffect, useState } from 'react';
import drawGraphOnCanvas from '../MathTools/drawGraph';

/**
 * InteractiveGraphSection – an interactive graph demo using inputs.
 * Allows the user to change numeric values that update a bar-graph canvas.
 */
export function InteractiveGraphSection({ graphCanvasRef, initialData }) {
  const [graphData, setGraphData] = useState(initialData);

  // Redraw the graph when data changes
  useEffect(() => {
    drawGraphOnCanvas(graphCanvasRef.current, graphData);
  }, [graphData, graphCanvasRef]);

  return (
    <div>
      {/* Input controls for each graph data item */}
      {graphData.map((item, i) => (
        <div key={i} style={{ marginBottom: "5px" }}>
          <label>
            {item.label}:{" "}
            <input
              type="number"
              value={item.value}
              onChange={(e) => {
                const newData = [...graphData];
                newData[i].value = parseInt(e.target.value, 10) || 0;
                setGraphData(newData);
              }}
              style={{ width: "50px" }}
            />
          </label>
        </div>
      ))}

      {/* The bar-graph canvas */}
      <div className="graph-container" style={{ marginTop: 20 }}>
        <canvas
          ref={graphCanvasRef}
          width="300"
          height="200"
          className="graph-canvas"
        />
      </div>

      <p>
        The graph shows each category on the horizontal axis and the number of
        votes on the vertical axis.
      </p>
    </div>
  );
}

/**
 * drawShapeOnCanvas – simple 2D shape drawing helper.
 * We cycle through triangle, square, circle, pentagon, hexagon.
 */
export function drawShapeOnCanvas(canvas, idx) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = "#007bff";
  ctx.lineWidth = 3;
  ctx.beginPath();

  switch (idx) {
    case 0: // Triangle
      ctx.moveTo(100, 20);
      ctx.lineTo(50, 110);
      ctx.lineTo(150, 110);
      ctx.closePath();
      break;

    case 1: // Square
      ctx.rect(60, 40, 80, 80);
      break;

    case 2: // Circle
      ctx.arc(100, 80, 40, 0, Math.PI * 2);
      break;

    case 3: // Pentagon
      ctx.moveTo(100, 20);
      ctx.lineTo(50, 60);
      ctx.lineTo(70, 120);
      ctx.lineTo(130, 120);
      ctx.lineTo(150, 60);
      ctx.closePath();
      break;

    default: // Hexagon
      ctx.moveTo(100, 20);
      ctx.lineTo(60, 50);
      ctx.lineTo(60, 90);
      ctx.lineTo(100, 120);
      ctx.lineTo(140, 90);
      ctx.lineTo(140, 50);
      ctx.closePath();
      break;
  }

  ctx.stroke();
}
export function generateQuizQuestion(forcedTopic = null) {
  // Topics (if forcedTopic is given, we only use that)
  const topics = forcedTopic
    ? [forcedTopic]
    : [
        "numberOps",
        "patternsAlgebra",
        "measurement",
        "geometry",
        "dataProbability",
        "financialLit",
      ];

  const topic = topics[Math.floor(Math.random() * topics.length)];
  let q = "";
  let a;
  let opts = [];

  /* 1️⃣ Number Ops (Enhanced for Grade4) */
  if (topic === "numberOps") {
    // Possible subtopics for Grade 4
    const subPool = ["add", "sub", "mul", "mul2d", "div", "frac"];
    const sub = subPool[Math.floor(Math.random() * subPool.length)];

    if (sub === "add") {
      // 4-digit addition
      const n1 = Math.floor(Math.random() * 9000) + 1000; // 1000–9999
      const n2 = Math.floor(Math.random() * 9000) + 1000;
      a = n1 + n2;

      // Build a text-based question
      const line = "‾".repeat(Math.max(String(n1).length, String(n2).length) + 2);
      q =
        `${String(n1).padStart(6, " ")}` +
        `\n+${String(n2).padStart(5, " ")}` +
        `\n${line}`;

      opts = [a, a + 10, a - 10];
    } else if (sub === "sub") {
      // 4-digit subtraction
      let n1 = Math.floor(Math.random() * 9000) + 1000;
      let n2 = Math.floor(Math.random() * 9000) + 1000;
      if (n2 > n1) [n1, n2] = [n2, n1]; // ensure n1 >= n2
      a = n1 - n2;

      const line = "‾".repeat(Math.max(String(n1).length, String(n2).length) + 2);
      q =
        `${String(n1).padStart(6, " ")}` +
        `\n−${String(n2).padStart(5, " ")}` +
        `\n${line}`;

      opts = [a, a + 10, Math.max(0, a - 10)];
    } else if (sub === "mul") {
      // 1-digit × 2-digit
      const num1 = Math.floor(Math.random() * 9) + 2; // up to 10..12
      const num2 = Math.floor(Math.random() * 12) + 2;
      a = num1 * num2;
      q = `${num1} × ${num2} = ?`;

      opts = [a, a + 5, Math.max(1, a - 5)];
    } else if (sub === "mul2d") {
      // 2-digit × 2-digit
      const n1 = Math.floor(Math.random() * 40) + 10; // 10..49
      const n2 = Math.floor(Math.random() * 40) + 10;
      a = n1 * n2;
      q = `What is ${n1} × ${n2}?`;

      opts = [a, a + 10, Math.max(1, a - 10)];
    } else if (sub === "div") {
      // possibly a remainder
      const divisor = Math.floor(Math.random() * 12) + 2; 
      const quotient = Math.floor(Math.random() * 15) + 2; 
      const dividend = divisor * quotient + Math.floor(Math.random() * divisor);

      // We'll ask for integer quotient ignoring remainder
      const integerQuotient = Math.floor(dividend / divisor);
      q = `${dividend} ÷ ${divisor} = ? (integer quotient)`;
      a = integerQuotient;

      opts = [a, a + 1, Math.max(0, a - 1)];
    } else {
      // fraction question
      // compare or multiply a mixed fraction
      const style = Math.random() < 0.5 ? "compare" : "multiplyMixed";

      if (style === "compare") {
        // Compare two bigger denominators
        const n1 = Math.floor(Math.random() * 4) + 2; // 2..5
        const d1 = Math.floor(Math.random() * 6) + 4; // 4..9
        const n2 = Math.floor(Math.random() * 4) + 2;
        const d2 = Math.floor(Math.random() * 6) + 4;

        const frac1 = `${n1}/${d1}`;
        const frac2 = `${n2}/${d2}`;
        const val1 = n1 / d1;
        const val2 = n2 / d2;

        a = val1 > val2 ? frac1 : frac2;
        q = `Which fraction is greater?\n${frac1} or ${frac2}?`;
        opts = [frac1, frac2];
      } else {
        // multiply a mixed fraction by a fraction
        const whole = Math.floor(Math.random() * 2) + 1; // 1..2
        const nn = Math.floor(Math.random() * 2) + 1; // 1..2
        const dd = [2, 3, 4][Math.floor(Math.random() * 3)];
        const top1 = whole * dd + nn; // convert to improper fraction

        const n2 = Math.floor(Math.random() * 3) + 1; 
        const d2 = [2, 3, 4][Math.floor(Math.random() * 3)];
        const resultNum = top1 * n2;
        const resultDen = dd * d2;

        q = `Multiply the fractions:\n${whole} ${nn}/${dd} × ${n2}/${d2} = ? (improper)`;
        a = `${resultNum}/${resultDen}`;
        opts = [
          a,
          `${resultNum + 1}/${resultDen}`,
          `${Math.max(1, resultNum - 1)}/${resultDen}`,
        ];
      }
    }
  }

  /* 2️⃣ Patterns & Algebra (Grade4) */
  else if (topic === "patternsAlgebra") {
    const subtypes = ["numberPattern", "twoStepPattern", "variable", "multiplesFactors"];
    const subtype = subtypes[Math.floor(Math.random() * subtypes.length)];

    if (subtype === "numberPattern") {
      // single-step pattern
      const start = Math.floor(Math.random() * 5) + 3;
      const step = Math.floor(Math.random() * 4) + 2;
      const seq = [start, start + step, start + 2 * step, start + 3 * step];
      q = `What is the next number in the pattern?\n${seq.join(", ")}, ____`;
      a = seq[3] + step;
      opts = [a, a + step, Math.max(1, a - step)];
    } else if (subtype === "twoStepPattern") {
      // We want the pattern +4, then ×2, repeated.
      // We'll do 4 operations in the loop so the 5th operation is "next".
      
      const start = 3; // or random in [2..6]
      // We have 5 total operations, so the 5th is the "next" step.
      const ops = ["+4", "×2", "+4", "×2", "+4"]; 
      let arr = [start];
      let current = start;
    
      // Apply the first 4 ops => get 5 terms in arr
      // i=0 => +4, i=1 => ×2, i=2 => +4, i=3 => ×2
      for (let i = 0; i < 4; i++) {
        if (ops[i] === "+4") {
          current += 4;
        } else {
          current *= 2;
        }
        arr.push(current);
      }
    
      // Now the array ends in 36 after 4 ops (3→7→14→18→36).
      // The 5th operation (ops[4]) is “+4”.
      let nextOp = ops[4];
      let nextVal;
      if (nextOp === "+4") {
        nextVal = current + 4;
      } else {
        nextVal = current * 2;
      }
    
      // The question shows the 5 terms [3,7,14,18,36], then asks for nextVal => 40
      q = `Two-step pattern: +4, then ×2, repeat.\nSequence: ${arr.join(", ")}. Next?`;
      a = nextVal;
    
      // Provide distractors
      const d1 = a + 2;
      const d2 = a + 4;
      opts = [a, d1, d2];
      opts.sort(() => Math.random() - 0.5);
    } else if (subtype === "variable") {
      // x + something = bigger number
      let addend = Math.floor(Math.random() * 20) + 5;
      let sum = Math.floor(Math.random() * 30) + 20;
      if (sum <= addend) [addend, sum] = [5, 25];
      a = sum - addend;
      q = `x + ${addend} = ${sum}. Solve for x.`;
      opts = [a, a + 1, Math.max(0, a - 1)];
    } else {
      // multiplesFactors
      const base = Math.floor(Math.random() * 9) + 2; // 2..10
      const multiplier = Math.floor(Math.random() * 5) + 2; 
      const multiple = base * multiplier;

      q = `Which number is a multiple of ${base}?`;
      a = multiple;

      // fix for only one correct multiple
      const distractors = [];
      while (distractors.length < 2) {
        const candidate = Math.floor(Math.random() * 50) + 2;
        if (candidate !== multiple && candidate % base !== 0) {
          distractors.push(candidate);
        }
      }
      opts = [multiple, ...distractors];
      opts.sort(() => Math.random() - 0.5);
    }
  }

  /* 3️⃣ Measurement (Grade4) */
  else if (topic === "measurement") {
    const sub = ["convert", "perimeter", "area", "elapsed", "compare"][
      Math.floor(Math.random() * 5)
    ];

    if (sub === "convert") {
      const conversions = [
        ["m", "cm", 100],
        ["km", "m", 1000],
        ["L", "mL", 1000],
        ["kg", "g", 1000],
        ["min", "sec", 60],
      ];
      const [from, to, factor] = conversions[Math.floor(Math.random() * conversions.length)];
      const value = Math.floor(Math.random() * 5) + 1; 
      q = `How many ${to} in ${value} ${from}?`;
      a = value * factor;
      opts = [a, a + factor, Math.max(1, a - factor)];
    } else if (sub === "perimeter") {
      const l = Math.floor(Math.random() * 15) + 5;
      const w = Math.floor(Math.random() * 10) + 2;
      a = 2 * (l + w);
      q = `A rectangle is ${l} m by ${w} m. What is its perimeter in meters?`;
      opts = [a, a + 2, Math.max(1, a - 2)];
    } else if (sub === "area") {
      const length = Math.floor(Math.random() * 10) + 5;
      const width = Math.floor(Math.random() * 10) + 3;
      a = length * width;
      q = `What is the area of a ${length} cm × ${width} cm rectangle?`;
      opts = [a, a + 5, Math.max(1, a - 5)];
    } else if (sub === "elapsed") {
      const startH = [7, 9, 10, 12, 13, 14][Math.floor(Math.random() * 6)];
      const durationMin = [45, 60, 75, 90, 120][Math.floor(Math.random() * 5)];
      const endH = startH + Math.floor(durationMin / 60);
      const endM = durationMin % 60;
      q = `A lesson starts at ${startH}:00 and ends at ${endH}:${String(
        endM
      ).padStart(2, "0")}. How many minutes long is it?`;
      a = durationMin;
      opts = [a, a + 15, Math.max(15, a - 15)];
    } else {
      // compare mass or length
      const items = [
        ["a basketball", 600], // grams
        ["a textbook", 1200], // 1.2 kg
        ["a pencil", 15],
      ];
      const [name, massG] = items[Math.floor(Math.random() * items.length)];
      const guess = Math.floor(Math.random() * 2000) + 50;
      q = `Is ${name} likely heavier than ${guess} grams?`;
      a = massG > guess ? "Yes" : "No";
      opts = ["Yes", "No"];
    }
  }

  /* 4️⃣ Geometry (Grade4) */
  else if (topic === "geometry") {
    const sub = ["sides", "typesTriangles", "angles", "parallel"][
      Math.floor(Math.random() * 4)
    ];

    if (sub === "sides") {
      // polygons up to decagon
      const shapes = [
        ["triangle", 3],
        ["square", 4],
        ["pentagon", 5],
        ["hexagon", 6],
        ["octagon", 8],
        ["decagon", 10],
      ];
      const [shape, sides] = shapes[Math.floor(Math.random() * shapes.length)];
      q = `How many sides does a ${shape} have?`;
      a = sides;
      opts = [sides, sides + 1, Math.max(1, sides - 1)];
    } else if (sub === "typesTriangles") {
      // classify triangle
      const pool = [
        ["Is a triangle with angles 90°, 45°, 45° a right triangle?", "Yes"],
        ["Is a triangle with angles 60°, 60°, 60° an equilateral triangle?", "Yes"],
        ["Is a triangle with angles 100°, 40°, 40° an obtuse triangle?", "Yes"],
      ];
      const picked = pool[Math.floor(Math.random() * pool.length)];
      q = picked[0];
      a = picked[1];
      opts = ["Yes", "No"];
    } else if (sub === "angles") {
      const pool = [
        ["How many degrees is a right angle?", 90],
        ["How many degrees in a straight line?", 180],
        ["How many degrees in a full turn (circle)?", 360],
      ];
      const chosen = pool[Math.floor(Math.random() * pool.length)];
      q = chosen[0];
      a = chosen[1];
      opts = [a, a + 10, Math.max(0, a - 10)];
    } else {
      const pool = [
        ["Do parallel lines ever intersect?", "No"],
        ["Do perpendicular lines form right angles?", "Yes"],
      ];
      const chosen = pool[Math.floor(Math.random() * pool.length)];
      q = chosen[0];
      a = chosen[1];
      opts = ["Yes", "No"];
    }
  }

  /* 5️⃣ Data & Probability (Grade4) */
  else if (topic === "dataProbability") {
    const sub = ["vocab", "votes", "realLife", "compare", "graph"][
      Math.floor(Math.random() * 5)
    ];

    if (sub === "vocab") {
      const pool = [
        ["Which word means 0 % chance?", "impossible"],
        ["Which word means 100 % chance?", "certain"],
        ["Which word means more than 50 % chance?", "likely"],
        ["Which word means less than 50 % chance?", "unlikely"],
      ];
      const chosen = pool[Math.floor(Math.random() * pool.length)];
      q = chosen[0];
      a = chosen[1];
      const allWords = ["certain", "likely", "unlikely", "impossible"].filter((w) => w !== a);
      const distractors = allWords.slice(0, 2);
      opts = [a, ...distractors].sort(() => Math.random() - 0.5);
    } else if (sub === "votes") {
      const dataSet = [
        { soccer: 12, basketball: 8 },
        { red: 3, blue: 9 },
        { apple: 10, banana: 6 },
      ];
      const votes = dataSet[Math.floor(Math.random() * dataSet.length)];
      const keys = Object.keys(votes);
      a = votes[keys[0]] > votes[keys[1]] ? keys[0] : keys[1];
      q = `Survey results: ${keys[0]} (${votes[keys[0]]}) vs ${keys[1]} (${votes[keys[1]]}). Which is more popular?`;
      opts = [keys[0], keys[1]];
    } else if (sub === "realLife") {
      const pool = [
        ["What's more likely: rain or snow in July?", "rain"],
        ["What's more likely: rolling an odd or an even number on a standard die?", "equally likely"],
        ["What's more likely: picking a heart or a diamond from a standard deck?", "equally likely"],
      ];
      const picked = pool[Math.floor(Math.random() * pool.length)];
      if (picked[1] === "equally likely") {
        q = picked[0];
        a = "equally likely";
        opts = ["rain", "snow", "equally likely"];
      } else {
        q = picked[0];
        a = picked[1];
        opts = [a, "snow", "rolling a 6", "blue square"].slice(0, 3);
        if (!opts.includes(a)) opts[0] = a;
      }
    } else if (sub === "compare") {
      let red = Math.floor(Math.random() * 10) + 1;
      let blue = Math.floor(Math.random() * 10) + 1;
      while (red === blue) {
        blue = Math.floor(Math.random() * 10) + 1;
      }
      a = red > blue ? "red" : "blue";
      q = `There are ${red} red marbles and ${blue} blue marbles. Which is more likely to be picked?`;
      opts = ["red", "blue"];
    } else {
      const bars = [
        { apples: 12, oranges: 8 },
        { cats: 7, dogs: 11 },
        { bikes: 5, cars: 9 },
        { lions: 2, giraffs: 5 }
      ];
      const selected = bars[Math.floor(Math.random() * bars.length)];
      const entries = Object.entries(selected);
      const [k1, v1] = entries[0];
      const [k2, v2] = entries[1];
      a = v1 > v2 ? k1 : k2;
      q = `Bar graph data: ${k1} (${v1}) vs ${k2} (${v2}). Which has more?`;
      opts = [k1, k2];
    }
  }

  /* 6️⃣ Financial Literacy (Grade4) */
  else if (topic === "financialLit") {
    const sub = Math.floor(Math.random() * 6);
    if (sub === 0) {
      const have = Math.floor(Math.random() * 10) + 5; 
      const toy = Math.floor(Math.random() * (have + 3)) + 2;
      const snack = Math.floor(Math.random() * (have + 3)) + 1;
      q = `You have $${have}. A toy costs $${toy} and a snack costs $${snack}. Can you buy both?`;
      a = toy + snack <= have ? "Yes" : "No";
      opts = ["Yes", "No"];
    } else if (sub === 1) {
      const costPool = [8, 12, 15, 20, 25];
      const cost = costPool[Math.floor(Math.random() * costPool.length)];
      const have = Math.floor(Math.random() * cost);
      q = `You want an item that costs $${cost}, but you have $${have}. How much more do you need?`;
      a = cost - have;
      opts = [a, a + 1, Math.max(1, a - 1)];
    } else if (sub === 2) {
      const paid = [10, 20, 50][Math.floor(Math.random() * 3)];
      const cost = Math.floor(Math.random() * (paid - 2)) + 2;
      q = `You pay $${paid} for an item costing $${cost}. How much change do you get?`;
      a = paid - cost;
      opts = [a, a + 1, Math.max(0, a - 1)];
    } else if (sub === 3) {
      const item = [20, 25, 30, 40][Math.floor(Math.random() * 4)];
      const save = [2, 5, 10][Math.floor(Math.random() * 3)];
      q = `An item costs $${item}. You save $${save} each week. How many weeks do you need?`;
      a = Math.ceil(item / save);
      opts = [a, a + 1, Math.max(1, a - 1)];
    } else if (sub === 4) {
      const toonies = Math.floor(Math.random() * 5); 
      const loonies = Math.floor(Math.random() * 5) + 1; 
      const quarters = Math.floor(Math.random() * 4) + 1; 
      const total = toonies * 2 + loonies + quarters * 0.25;
      q = `You have ${toonies} toonies, ${loonies} loonies, and ${quarters} quarters. How much in total?`;
      a = parseFloat(total.toFixed(2));
      opts = [
        a,
        parseFloat((a + 0.25).toFixed(2)),
        parseFloat((a - 0.25).toFixed(2)),
      ];
    } else {
      const totals = [20, 25, 30];
      const total = totals[Math.floor(Math.random() * totals.length)];
      const spent = [5, 8, 10, 15][Math.floor(Math.random() * 4)];
      const spent2 = [2, 3, 5][Math.floor(Math.random() * 3)];
      const totalSpent = spent + spent2;

      q = `You have $${total}. You spend $${spent} on lunch and $${spent2} on a toy. How much is left?`;
      a = total - totalSpent;
      opts = [a, a + 1, Math.max(0, a - 1)];
    }
  }

  // final shuffle if not just Yes/No
  if (!(opts.length === 2 && opts.includes("Yes"))) {
    opts.sort(() => Math.random() - 0.5);
  }

  return { question: q, correctAnswer: a, options: opts };
}